import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDProgress from "components/MDProgress";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import { sotApi } from "api/sot";
import { giaoDichApi } from "api/giaoDich";
import { nguoiDungApi } from "api/nguoiDung";
import { nhanTrangThaiSot } from "lib/nhanTrangThaiSot";
import QrCodeImage from "components/QrCodeImage";
import TaiXuongQr from "components/TaiXuongQr";
import useTour from "hooks/useTour";
import buocSotChiTiet from "tours/sotChiTiet";

function gioHienThi(iso) {
  return iso ? new Date(iso).toLocaleString("vi-VN") : "—";
}

// Nhãn nhỏ + giá trị — lặp lại ở cả ô sức chứa lẫn bảng đối chiếu cân lại,
// gom thành một hàm để hai chỗ không trôi mỗi nơi một cỡ chữ.
function Dong({ nhan, giaTri, mau, co }) {
  return (
    <Grid item {...co}>
      <MDTypography variant="overline" color="text" lineHeight={1}>
        {nhan}
      </MDTypography>
      <MDTypography variant="h6" color={mau} mt={0.25}>
        {giaTri}
      </MDTypography>
    </Grid>
  );
}

Dong.propTypes = {
  nhan: PropTypes.string.isRequired,
  giaTri: PropTypes.node.isRequired,
  mau: PropTypes.string,
  co: PropTypes.objectOf(PropTypes.number),
};

Dong.defaultProps = {
  mau: "dark",
  co: { xs: 6 },
};

function ChiTietSot() {
  const { id } = useParams();
  const [sot, setSot] = useState(null);
  const [danhSachPO, setDanhSachPO] = useState([]);
  const [tenNguoiNhapKho, setTenNguoiNhapKho] = useState(null);

  useEffect(() => {
    setSot(null);
    setDanhSachPO([]);
    setTenNguoiNhapKho(null);

    const huySot = sotApi.langNgheChiTiet(id, async (p) => {
      setSot(p);
      if (p?.trangThai === "da_nhap_kho" && p.nguoiNhapKho) {
        // firestore.rules chỉ cho admin/kế toán đọc nguoiDung của người khác —
        // nhân viên thu mua (được phép vào trang này) bị permission-denied ở
        // đây. Không để cả trang kẹt "Đang tải..." vì mỗi tên người nhập kho,
        // âm thầm bỏ qua và để trống là đủ.
        try {
          const nd = await nguoiDungApi.layMot(p.nguoiNhapKho);
          setTenNguoiNhapKho(nd?.hoTen ?? nd?.email ?? null);
        } catch {
          setTenNguoiNhapKho(null);
        }
      } else {
        setTenNguoiNhapKho(null);
      }
    });

    const huyGd = giaoDichApi.langNgheTheoSot(id, setDanhSachPO);

    return () => {
      huySot();
      huyGd();
    };
  }, [id]);

  // 1 PO có thể gồm nhiều loại bơ VÀ trải nhiều sọt khác nhau (khi 1 sọt đầy
  // giữa chừng lúc đang cân, xem giaoDichApi.taoMoi/suaPO) — giaoDichApi.theoSot
  // trả về PO chạm tới sọt này dù chỉ 1 phần dòng thuộc về nó, nên phải lọc
  // lại còn ĐÚNG các dòng của SỌT NÀY trước khi flatten, để tổng kg trên
  // trang 1 sọt không cộng nhầm khối lượng thuộc sọt khác trong cùng PO.
  const giaoDichList = danhSachPO.flatMap((po) =>
    po.items.filter((it) => it.maSot === id).map((it) => ({ ...po, ...it }))
  );

  useTour("sot-chi-tiet", !sot ? [] : buocSotChiTiet());

  if (!sot) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDTypography variant="button" color="text">
            Đang tải...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const nhan = nhanTrangThaiSot(sot.trangThai);
  const phanTram = Math.min(100, Math.round((sot.khoiLuongHienTai / sot.khoiLuongToiDa) * 100));
  const chenhLechNhapKho =
    sot.khoiLuongCanLai != null ? sot.khoiLuongCanLai - sot.khoiLuongHienTai : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Sọt"
          tieuDe={sot.maSot}
          moTa="Sức chứa, đối chiếu cân lúc nhập kho, lịch sử giao dịch và mã QR để in dán."
        />

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card id="tour-sot-chi-tiet-suc-chua">
              <MDBox p={{ xs: 2.5, sm: 3 }}>
                <MDBox
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  mb={1.5}
                >
                  <MDTypography variant="h5" component="h2">
                    Sức chứa
                  </MDTypography>
                  <MDBadge
                    badgeContent={nhan.label}
                    color={nhan.color}
                    variant="gradient"
                    size="sm"
                  />
                </MDBox>

                <MDBox display="flex" alignItems="baseline" gap={0.75}>
                  <MDTypography variant="h3" lineHeight={1}>
                    {sot.khoiLuongHienTai}
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="regular" color="text">
                    / {sot.khoiLuongToiDa} kg — đã dùng {phanTram}%
                  </MDTypography>
                </MDBox>

                <MDBox mt={1.5} mb={2.5}>
                  <MDProgress
                    value={phanTram}
                    color={phanTram >= 100 ? "error" : phanTram >= 90 ? "warning" : "info"}
                    variant="gradient"
                  />
                </MDBox>

                <MDBox
                  pt={2}
                  sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[300]}` })}
                >
                  <Grid container spacing={2}>
                    <Dong nhan="Số giao dịch" giaTri={sot.soGiaoDich} />
                    {/* Khối lượng bì chỉ là thông tin tham khảo, Sọt cũ có thể
                        chưa nhập — ẩn hẳn ô này thay vì hiện dấu gạch cho gọn. */}
                    {sot.khoiLuongBi != null && (
                      <Dong nhan="Khối lượng bì" giaTri={`${sot.khoiLuongBi} kg`} />
                    )}
                  </Grid>
                </MDBox>
              </MDBox>
            </Card>

            {sot.trangThai === "da_nhap_kho" && sot.khoiLuongCanLai != null && (
              <MDBox mt={3}>
                <Card>
                  <MDBox p={{ xs: 2.5, sm: 3 }}>
                    <NhanDau>Đối chiếu</NhanDau>
                    <MDTypography variant="h5" component="h2" mb={2}>
                      Cân lại lúc nhập kho
                    </MDTypography>
                    <Grid container spacing={2}>
                      <Dong
                        nhan="Lúc thu mua"
                        giaTri={`${sot.khoiLuongHienTai} kg`}
                        co={{ xs: 6, sm: 4 }}
                      />
                      <Dong
                        nhan="Cân lại nhập kho"
                        giaTri={`${sot.khoiLuongCanLai} kg`}
                        co={{ xs: 6, sm: 4 }}
                      />
                      <Dong
                        nhan="Chênh lệch"
                        co={{ xs: 12, sm: 4 }}
                        mau={
                          chenhLechNhapKho === 0 ? "text" : chenhLechNhapKho < 0 ? "error" : "info"
                        }
                        giaTri={`${chenhLechNhapKho > 0 ? "+" : ""}${chenhLechNhapKho} kg`}
                      />
                    </Grid>
                    <MDTypography variant="caption" color="text" display="block" mt={2}>
                      Nhập kho lúc {gioHienThi(sot.nhapKhoLuc)}
                      {tenNguoiNhapKho ? ` — ${tenNguoiNhapKho}` : ""}
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            )}

            <MDBox mt={3}>
              <Card id="tour-sot-chi-tiet-lich-su">
                <MDBox
                  px={{ xs: 2.5, sm: 3 }}
                  py={2}
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                >
                  <MDTypography variant="h5" component="h2">
                    Lịch sử giao dịch
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {giaoDichList.length} dòng
                  </MDTypography>
                </MDBox>

                {giaoDichList.length === 0 ? (
                  <MDBox px={2.5} pb={4} pt={1} textAlign="center">
                    <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>
                      inventory_2
                    </Icon>
                    <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                      Chưa có giao dịch nào trong Sọt này.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                      Sọt sẽ có dòng đầu tiên ngay khi được dùng trong một lượt thu mua.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <List sx={{ py: 0 }}>
                    {giaoDichList.map((gd, i) => (
                      <MDBox key={`${gd.id}_${i}`}>
                        <ListItem
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                            py: 1.5,
                            px: { xs: 2.5, sm: 3 },
                          }}
                        >
                          <MDBox minWidth={0}>
                            <MDTypography variant="button" fontWeight="medium" display="block">
                              {gd.tenChuVuon}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {gd.tenVuon}
                              {gd.tenLoaiBo ? ` · ${gd.tenLoaiBo}` : ""}
                            </MDTypography>
                          </MDBox>
                          <MDTypography variant="button" color="text" sx={{ whiteSpace: "nowrap" }}>
                            {gd.khoiLuong} kg × {gd.donGia.toLocaleString("vi-VN")} đ
                          </MDTypography>
                        </ListItem>
                        {i < giaoDichList.length - 1 && <Divider sx={{ my: 0 }} />}
                      </MDBox>
                    ))}
                  </List>
                )}
              </Card>
            </MDBox>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card id="tour-sot-chi-tiet-qr">
              <MDBox p={{ xs: 2.5, sm: 3 }}>
                <NhanDau>In dán</NhanDau>
                <MDTypography variant="h5" component="h2">
                  Mã QR Sọt
                </MDTypography>
                <MDTypography variant="button" fontWeight="regular" color="text" display="block">
                  In và dán lên thành Sọt để nhân viên quét khi thu mua và nhập/xuất kho.
                </MDTypography>
                <MDBox mt={2.5} display="flex" flexDirection="column" alignItems="center">
                  <QrCodeImage value={sot.qrCode} />
                  <TaiXuongQr value={sot.qrCode} tenFile={`sot-${sot.maSot}`} />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ChiTietSot;
